import 'core-js/actual'
import 'regenerator-runtime/runtime'
import 'react-hot-loader'
import './index.css'
import React, { useCallback, useMemo } from 'react'
import ReactDOM from 'react-dom/client'
import { ThemeProvider } from 'styled-components'
import { Router } from 'react-router-dom'
import { YMProvider, history, initI18n, isDevelopment } from '@rst/libs-ts'
import {
	$lead,
	$pattern,
	$theme,
	$footer,
	$head,
	$nav,
	$info,
	forceShowAllBlocks,
	$showAllBlocks,
	$locale,
	$langSetting,
	$connectedLangs,
} from './model'
import { useStore } from 'effector-react'
import { Render } from './features/render'
import { Meta } from './model/meta'
import { ThemeContextProvider } from '@rst/render-blocks/src/lib/theme.ctx'
import './animations.css'
import { ButtonCtxProvider, Footer, L10nCtxProvider } from '@rst/render-blocks'
import { SubHeader } from './features/render/molecules/sub-header'
import { setConfiguration } from 'react-grid-system'
import { DataColorApply, ImportFontDev, LinkHandlerProvider } from '@rst/ui-ts'
import { DevIcons } from '@features/render/utils/dev-icons'
import { initDayjs } from '@lib/dayjs'

const defineType = window.__setting__?.lang_setting?.type || 'path'

const i18nInstance = initI18n({
	ns: ['rb-common'],
	lang: window.__setting__?.lang || $locale.getState(),
	initReact: true,
	defineType,
})

initDayjs(i18nInstance)

setConfiguration({
	containerWidths: [540, 750, 960, 1170],
	gutterWidth: 0,
	maxScreenClass: 'xl',
})

window.localStorage.setItem('page-has-been-force-refreshed', 'false')

if (isDevelopment) {
	ImportFontDev()
}

export const App = () => {
	const theme = useStore($theme)
	const lead = useStore($lead)
	const separator = useStore($pattern)

	// const headerPos = useStore($head).head_pos

	const showAllBlocks = useStore($showAllBlocks)

	const locale = useStore($locale)
	const connectedLangs = useStore($connectedLangs)
	const linkHandler = useCallback(
		({ to, href }: { to: string; href: string }) => {
			if (to && defineType === 'path' && locale !== 'ru') {
				if (connectedLangs.length === 1 && connectedLangs[0] === 'ru') {
					return { to, href }
				}
				return { href, to: /^\/\w\w\//i.test(to) ? to : `/${locale}${to}` }
			} else return { to, href }
		},
		[connectedLangs, locale]
	)

	const { availableLangs, select_pos, select, type } = useStore($langSetting)
	const langProps = useMemo(
		() =>
			availableLangs.length > 1
				? {
						lang: locale,
						availableLangs,
						selectType: select,
						localized: true,
						setLang: (lang: string) => {
							const { href, origin, host, protocol } = window.location
							if (type === 'path') {
								const regExp = new RegExp(`/${locale}($|/)`, 'i')
								if (regExp.test(href)) {
									const url = href.replace(
										regExp,
										lang === 'ru' ? '/' : `/${lang}/`
									)

									window.location.replace(url)
									return
								} else {
									// eslint-disable-next-line prefer-destructuring
									const rest = href.split(origin)[1]
									if (lang === 'ru') {
										window.location.replace(href)
										return
									}
									window.location.replace(`${origin}/${lang}${rest}`)
									return
								}
							} else {
								let parts = host.split('.')
								parts = parts.length === 3 ? [parts[1], parts[2]] : parts
								// eslint-disable-next-line prefer-destructuring
								const rest = href.split(origin)[1]
								if (lang === 'ru') {
									window.location.replace(
										`${protocol}//${parts[0]}.${parts[1]}${rest}`
									)
								}
								window.location.replace(
									`${protocol}//${lang}.${parts[0]}.${parts[1]}${rest}`
								)
							}
						},
				  }
				: null,
		[availableLangs, locale, select, type]
	)
	return (
		<>
			{isDevelopment && <DevIcons />}
			<YMProvider>
				<LinkHandlerProvider handler={linkHandler}>
					<L10nCtxProvider lang={locale} availableLangs={connectedLangs}>
						<ThemeProvider theme={{ ...theme, minPadding: 80 }}>
							<ThemeContextProvider value={{ separator, lead }}>
								<ButtonCtxProvider
									value={{
										forceShowAllBlocks: showAllBlocks,
										setForceShowAllBlocks: forceShowAllBlocks,
									}}
								>
									<SubHeader />

									<Render langProps={langProps} />
									<Footer
										langProps={
											['footer', 'header-footer'].includes(select_pos)
												? langProps
												: null
										}
										ctx={{
											$nav,
											$info,
											$head,
											$footer,
											$lead,
										}}
									/>
									<div id={'modal-root'} />
									<div id="awesome-modal-root" />
									<Meta />
									<DataColorApply $theme={$theme} />
								</ButtonCtxProvider>
							</ThemeContextProvider>
						</ThemeProvider>
					</L10nCtxProvider>
				</LinkHandlerProvider>
			</YMProvider>
		</>
	)
}

const root = ReactDOM.createRoot(
	document.getElementById('main-root') as HTMLDivElement
)

root.render(
	// @ts-ignore
	<Router history={history}>
		<App />
	</Router>
)
